// =====================================================================
// bordereaux/BordereauxAuto.jsx — M-015 « Remise transporteur », page UNIQUE (28-10-2026, maquette validée par Silviu)
// =====================================================================
// Avant (27/10) : un onglet « ⏰ Automatique » à part. Silviu, 28/10 : « je croyais que tu faisais la page dans Remise …
// ne polluons pas » → une seule page, rien de perdu :
//   - ListeRemise : la liste « Quel transporteur est au quai ? » EST le tableau des colis (EffiTrace : colis / contrôlés /
//     à contrôler) + « à signer » + heure automatique ; « ⚡ Générer + imprimer pour tous », 🖨 par ligne (PC),
//     « ↻ Relire EffiTrace », « Même heure pour tous » (droit Paramétrer), journal des générations.
//   - ReglagesAuto : dans la fiche du transporteur, « ⚙ Génération automatique » (heures lun–ven, exemplaires, réessai).
// PDA (Vincent au quai) : une colonne, cibles ≥ 42 px, colonnes secondaires et réglages repliés.
// Droits : lire = bordereaux.read/sign/auto ; générer = bordereaux.sign.all ; régler = bordereaux.auto.all.
// =====================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { bordereaux as api, errMsg } from '../../services/api.js';
import { C, teinte } from './theme.js';
import { JOURS, heureBxl, resumeReglage, texteJournal, LIBELLE_MODE, lignesListe, ANNULATION_LOT, SORTIE_LOT, texteEchec } from './auto.js';

const card = { background: C.surface, border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 13px', marginBottom: 12 };
const k = { fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: C.ink3, fontWeight: 600 };
const mono = { fontFamily: 'ui-monospace, "IBM Plex Mono", monospace', fontVariantNumeric: 'tabular-nums' };
const th = { textAlign: 'right', fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: C.ink3, fontWeight: 600, padding: '6px 4px', verticalAlign: 'bottom' };
const td = { padding: '9px 4px', borderTop: '1px solid #EDF2F5', textAlign: 'right' };
const btn = { padding: '9px 14px', borderRadius: 9, border: 0, background: C.accent, color: '#fff', font: 'inherit', fontWeight: 600, cursor: 'pointer' };
const btn2 = { padding: '7px 11px', borderRadius: 8, border: `1.5px solid ${C.lineStrong}`, background: '#fff', color: C.ink, font: 'inherit', cursor: 'pointer' };
const TONS = { ok: [C.ok, C.okSoft], warn: [C.warn, C.warnSoft], crit: [C.crit, C.critSoft], neutre: [C.ink2, C.surface2] };
const msgStyle = (ton) => ({ fontSize: 12.5, padding: '7px 10px', borderRadius: 8, marginTop: 8, color: TONS[ton][0], background: TONS[ton][1] });

// PDA : une colonne, cibles plus grandes, colonnes secondaires masquées (elles sont dans la fiche)
const CSS = `
.br-col-g { flex: 1 1 640px; min-width: 0; }
.br-col-d { flex: 0 1 380px; min-width: 300px; }
.br-ligne:hover td { background: ${C.surface2}; }
@media (max-width: 600px) {
  .br-col-g, .br-col-d { min-width: 0; flex-basis: 100%; }
  .br-pc { display: none; }
  .ba-cible { min-height: 42px; font-size: 15px !important; }
  .ba-chip { padding: 9px 13px !important; font-size: 14px !important; }
}`;
const ecranLarge = () => typeof window === 'undefined' || window.matchMedia('(min-width: 601px)').matches;


export function ListeRemise({ carriers, peutLancer, peutRegler, peutSansSignature, onChoisir, onRafraichir, occupe: occupeFiche }) {
  const [etat, setEtat] = useState(null);
  const [colis, setColis] = useState(null);
  const [lecture, setLecture] = useState(false);
  const [erreur, setErreur] = useState('');
  const [erreurColis, setErreurColis] = useState('');
  const [msg, setMsg] = useState(null);
  const [confirme, setConfirme] = useState(null);   // { tous: true } | { t: { carrierCode, carrierName } }
  const [autres, setAutres] = useState(false);
  const [annul, setAnnul] = useState(null);   // dialogue « annuler des bordereaux »
  const [sortie, setSortie] = useState(null);   // dialogue « sortir tout sans signature » (28-10-2026, temporaire)
  const etaitOccupe = useRef(false);
  const ficheEnOuverture = useRef(false);   // une fiche s'ouvre (connexion EffiTrace) : aucune autre lecture ne part
  ficheEnOuverture.current = !!occupeFiche;

  const chargerEtat = useCallback(async () => {
    try { setEtat(await api.auto()); setErreur(''); } catch (e) { setErreur(errMsg(e)); }
  }, []);
  const chargerColis = useCallback(async (force = false) => {
    setLecture(true);
    try { setColis(await api.autoColis(force)); setErreurColis(''); } catch (e) { setErreurColis(errMsg(e)); } finally { setLecture(false); }
  }, []);

  useEffect(() => { chargerEtat(); chargerColis(); }, [chargerEtat, chargerColis]);
  // journal vivant toutes les 15 s ; colis toutes les minutes (lecture refusée : reprise seule), et dès qu'une génération finit
  useEffect(() => {
    const t = setInterval(chargerEtat, 15_000);
    const c = setInterval(() => { if (!ficheEnOuverture.current) chargerColis(false); }, 60_000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [chargerEtat, chargerColis]);
  useEffect(() => {
    const occ = !!etat?.occupe;
    if (etaitOccupe.current && !occ) { chargerColis(); onRafraichir?.(); }
    etaitOccupe.current = occ;
  }, [etat, chargerColis, onRafraichir]);

  const lancer = async (corps) => {
    setConfirme(null); setMsg(null);
    try {
      const r = await api.autoLancer(corps);
      setMsg({ ton: 'ok', texte: r.lance === 'tous'
        ? 'Lancé pour tous les transporteurs qui ont des colis contrôlés : bordereaux créés puis imprimés, le résultat arrive dans le journal.'
        : 'Lancé : le résultat arrive dans le journal.' });
      setTimeout(chargerEtat, 1500);
    } catch (e) { setMsg({ ton: 'crit', texte: errMsg(e) }); }
  };

  // bordereaux à remettre (Zeus), avec la génération qui les a créés (journal) → dialogue d'annulation
  const ouvrirAnnulation = async (filtre, titre) => {
    setMsg(null);
    try {
      const groupes = await api.pending();
      const lotDe = new Map();
      // les exécutions « tous » qui se suivent à moins de 30 min = une seule génération, nommée par sa première heure
      const tous = (etat?.journal || []).filter((j) => j.mode === 'TOUS').sort((x, y) => new Date(x.debut) - new Date(y.debut));
      const debutTous = new Map();
      for (let i = 0, debut = null, prec = null; i < tous.length; i++) {
        const t = new Date(tous[i].debut).getTime();
        if (prec === null || t - prec > 30 * 60_000) debut = tous[i].debut;
        debutTous.set(tous[i], debut); prec = t;
      }
      for (const j of (etat?.journal || [])) {
        const nom = j.mode === 'TOUS' ? `${heureBxl(debutTous.get(j))} tous` : `${heureBxl(j.debut)} ${LIBELLE_MODE[j.mode] || ''}`.trim();
        for (const id of j.aAnnuler || []) if (!lotDe.has(id)) lotDe.set(id, nom);
      }
      const items = groupes.flatMap((g) => g.items.map((b) => ({ id: b.id, numero: b.numero, clientName: b.clientName, carrierName: b.carrierName, parcels: b.parcels, lot: lotDe.get(b.id), enVerification: !!b.annuleEtat })))
        .filter((b) => !filtre || filtre.includes(b.id));
      if (!items.length) { setMsg({ ton: 'warn', texte: 'Plus rien à annuler : ces bordereaux ont déjà été signés, sortis ou annulés.' }); return; }
      setAnnul({ titre, items, cocherTout: !!filtre, lots: filtre ? [] : [...new Set(items.map((b) => b.lot).filter(Boolean))] });
    } catch (err) { setMsg({ ton: 'crit', texte: errMsg(err) }); }
  };
  const apresAnnulation = (texte, ton = 'ok') => { setAnnul(null); setMsg({ ton, texte }); chargerEtat(); chargerColis(true); onRafraichir?.(); };

  // tous les bordereaux à faire signer (sauf « en vérification ») → dialogue de sortie sans signature
  const ouvrirSortie = async () => {
    setMsg(null);
    try {
      const groupes = await api.pending();
      const items = groupes.flatMap((g) => g.items.filter((b) => !b.annuleEtat)
        .map((b) => ({ id: b.id, numero: b.numero, clientName: b.clientName, carrierName: b.carrierName, parcels: b.parcels })));
      if (!items.length) { setMsg({ ton: 'warn', texte: 'Aucun bordereau à faire signer (hors annulations à contrôler) : rien à sortir.' }); return; }
      setSortie({ items });
    } catch (err) { setMsg({ ton: 'crit', texte: errMsg(err) }); }
  };
  const apresSortie = (texte, ton = 'ok') => { setSortie(null); setMsg({ ton, texte }); chargerEtat(); onRafraichir?.(); };

  // la liste ne dépend jamais de l'état « automatique » : sans lui (chargement, erreur), les transporteurs restent là
  const e = etat || { transporteurs: [], journal: [], occupe: null, impression: null, planificateurActif: true };
  const occupe = !!e.occupe;
  const { actives, autres: reste } = lignesListe(carriers, colis, e.transporteurs);
  const tot = { ...(colis?.total || { colis: 0, controles: 0, aControler: 0 }), pending: actives.reduce((s, l) => s + l.pending, 0) };
  const lu = !!colis?.luLe;

  return (
    <>
      <style>{CSS}</style>
      {erreur && <div style={{ ...msgStyle('crit'), marginBottom: 12 }}>{erreur}</div>}
      {!e.planificateurActif && (
        <div style={{ ...msgStyle('warn'), marginTop: 0, marginBottom: 12 }}>
          Cet environnement n'est pas la production : les réglages s'enregistrent, mais rien n'y est créé ni imprimé (ni à l'heure dite, ni avec les boutons) — EffiTrace est réel partout.
        </div>
      )}
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="br-col-g">
          <div style={card}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Quel transporteur est au quai ?</div>
            <div style={{ fontSize: 11.5, color: C.ink3, margin: '2px 0 10px' }}>
              Touchez un transporteur pour ouvrir sa fiche (générer, faire signer{peutRegler ? ', génération automatique' : ''}).
              Colis lus dans EffiTrace{lu ? <> à <span style={mono}>{heureBxl(colis.luLe)}</span></> : ''} ; seuls les colis <b>contrôlés</b> au packing peuvent aller sur un bordereau.
            </div>
            {peutLancer && (
              <button type="button" className="ba-cible" disabled={occupe || occupeFiche || !tot.controles}
                title={!tot.controles ? 'Aucun colis contrôlé' : occupe ? 'Une génération est en cours' : ''} onClick={() => setConfirme({ tous: true })}
                style={{ ...btn, width: '100%', padding: 13, fontSize: 15, marginBottom: 6, ...(occupe || !tot.controles ? { background: C.lineStrong, cursor: 'not-allowed' } : {}) }}>
                ⚡ Générer + imprimer pour tous{tot.controles ? ` (${tot.controles} colis contrôlés)` : ''}
              </button>
            )}
            {peutLancer && lu && !tot.controles && <div style={{ ...msgStyle('warn'), marginTop: 0 }}>Aucun colis contrôlé : rien à générer pour l'instant.</div>}
            {peutSansSignature && (
              <button type="button" className="ba-cible" disabled={occupe || occupeFiche || !tot.pending}
                title={!tot.pending ? 'Aucun bordereau à faire signer' : occupe ? 'Une génération est en cours' : ''} onClick={ouvrirSortie}
                style={{ ...btn2, width: '100%', padding: 12, fontSize: 15, marginBottom: 6, borderColor: '#E9C98F', color: C.warn,
                  ...(occupe || occupeFiche || !tot.pending ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
                🚚 Sortir tout sans signature
              </button>
            )}
            {msg && <div style={msgStyle(msg.ton)}>{msg.texte}</div>}
            {erreurColis && <div style={msgStyle('crit')}>Lecture des colis : {erreurColis}</div>}
            {colis?.erreur && <div style={msgStyle('warn')}>{lu ? 'Dernière lecture conservée — ' : 'EffiTrace n’a pas pu être lu — '}{colis.erreur}</div>}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, marginTop: 8 }}>
              <thead><tr>
                <th style={{ ...th, textAlign: 'left' }}>Transporteur</th><th style={th}>Colis</th><th style={th}>Contrôlés</th>
                <th style={th} className="br-pc">À contrôler</th><th style={th}>Signature</th><th style={th} className="br-pc">Auto</th>
                {peutLancer && <th style={th} className="br-pc">Générer</th>}
              </tr></thead>
              <tbody>
                {actives.length ? actives.map((l) => {
                  const pct = l.colis ? Math.round((100 * l.controles) / l.colis) : 0;
                  return (
                    <tr key={l.code} className="br-ligne" style={{ cursor: occupeFiche ? 'wait' : 'pointer' }} onClick={() => !occupeFiche && onChoisir({ code: l.code, name: l.name })}>
                      <td style={{ ...td, textAlign: 'left' }}>
                        <span style={{ display: 'inline-block', width: 5, height: 18, borderRadius: 2, verticalAlign: -4, marginRight: 8, background: teinte(l.name) }} />
                        <b>{l.name}</b>
                        {l.colis > 0 && <div style={{ height: 5, background: C.sunken, borderRadius: 3, overflow: 'hidden', marginTop: 4 }}><i style={{ display: 'block', height: '100%', width: `${pct}%`, background: C.ok }} /></div>}
                      </td>
                      <td style={{ ...td, ...mono }}>{l.colis || '—'}</td>
                      <td style={{ ...td, ...mono, color: C.ok }}>{l.controles || '—'}</td>
                      <td style={{ ...td, ...mono, color: l.aControler ? C.warn : C.ink3 }} className="br-pc">{l.aControler || '—'}</td>
                      <td style={td}>{l.pending ? <span style={{ ...mono, fontSize: 12.5, fontWeight: 700, color: C.accent, background: C.accentSoft, borderRadius: 20, padding: '2px 9px', whiteSpace: 'nowrap' }}>{l.pending} à signer</span> : '—'}</td>
                      <td style={{ ...td, ...mono, color: C.ink2 }} className="br-pc">{l.auto?.prochaine || l.auto?.prochainJour || 'manuel'}</td>
                      {peutLancer && (
                        <td style={td} className="br-pc">
                          {l.controles > 0 && (
                            <button type="button" style={btn2} disabled={occupe || occupeFiche} title="Générer + imprimer sans ouvrir la fiche"
                              onClick={(e) => { e.stopPropagation(); setConfirme({ t: { carrierCode: l.code, carrierName: l.name } }); }}>🖨 {l.controles}</button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={peutLancer ? 7 : 6} style={{ ...td, textAlign: 'center', color: C.ink3, padding: 18 }}>{!colis ? 'Lecture EffiTrace…' : lu ? 'Rien en attente : ni colis, ni bordereau à signer.' : 'Colis pas encore lus dans EffiTrace — aucun bordereau à signer.'}</td></tr>
                )}
              </tbody>
              <tfoot><tr>
                <td style={{ ...td, textAlign: 'left', fontWeight: 700, borderTop: `2px solid ${C.lineStrong}` }}>Total · {actives.length}</td>
                <td style={{ ...td, ...mono, fontWeight: 700, borderTop: `2px solid ${C.lineStrong}` }}>{lu ? tot.colis : '—'}</td>
                <td style={{ ...td, ...mono, fontWeight: 700, borderTop: `2px solid ${C.lineStrong}` }}>{lu ? tot.controles : '—'}</td>
                <td style={{ ...td, ...mono, fontWeight: 700, borderTop: `2px solid ${C.lineStrong}` }} className="br-pc">{lu ? tot.aControler : '—'}</td>
                <td style={{ ...td, ...mono, fontWeight: 700, borderTop: `2px solid ${C.lineStrong}` }}>{tot.pending || '—'}</td>
                <td style={{ ...td, borderTop: `2px solid ${C.lineStrong}` }} className="br-pc" />
                {peutLancer && <td style={{ ...td, borderTop: `2px solid ${C.lineStrong}` }} className="br-pc" />}
              </tr></tfoot>
            </table>
            {reste.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <button type="button" className="ba-cible" style={{ ...btn2, border: 0, color: C.accent, fontWeight: 600, padding: '6px 0' }} onClick={() => setAutres(!autres)}>
                  {autres ? '▴' : '▾'} Autres transporteurs ({reste.length}) — {lu ? 'rien en cours' : 'colis pas encore lus'}
                </button>
                {autres && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    {reste.map((l) => (
                      <button key={l.code} type="button" className="ba-cible" style={btn2} disabled={occupeFiche} onClick={() => onChoisir({ code: l.code, name: l.name })}>{l.name}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
              <button type="button" className="ba-cible" style={btn2} onClick={() => chargerColis(true)} disabled={lecture || occupeFiche}>
                {lecture ? '⏳ Lecture EffiTrace…' : '↻ Relire EffiTrace'}
              </button>
              {peutLancer && tot.pending > 0 && (
                <button type="button" className="ba-cible" style={{ ...btn2, color: C.crit, borderColor: '#E2B4AF' }} disabled={occupe || occupeFiche}
                  onClick={() => ouvrirAnnulation(null, 'Annuler des bordereaux non signés — tous les transporteurs')}>
                  ✖ Annuler des bordereaux non signés… ({tot.pending})
                </button>
              )}
            </div>
            {peutRegler && etat && <MemeHeurePourTous transporteurs={etat.transporteurs} onEnregistre={chargerEtat} />}
            <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 8 }}>« Colis » = UM en attente chez EffiTrace (palettes comprises). Sur PDA, « À contrôler » et l'heure automatique sont dans la fiche.</div>
          </div>
        </div>
        <div className="br-col-d">
          <Journal journal={e.journal} occupe={e.occupe} impression={e.impression}
            onAnnulerGeneration={peutLancer && !occupe ? (j) => ouvrirAnnulation(j.aAnnuler, `Annuler la génération de ${heureBxl(j.debut)} (${j.aAnnuler.length} bordereau${j.aAnnuler.length > 1 ? 'x' : ''}) ?`) : null} />
        </div>
      </div>
      {annul && <AnnulerDialog {...annul} onFermer={() => setAnnul(null)} onFait={apresAnnulation} />}
      {sortie && <SortieDialog items={sortie.items} onFermer={() => setSortie(null)} onFait={apresSortie} />}
      {confirme && (
        <Confirmation confirme={confirme} colis={colis} onAnnuler={() => setConfirme(null)}
          onOk={() => lancer(confirme.tous ? { tous: true } : { carrierCode: confirme.t.carrierCode, carrierName: confirme.t.carrierName })} />
      )}
    </>
  );
}

function MemeHeurePourTous({ transporteurs, onEnregistre }) {
  const [heure, setHeure] = useState('16:15');
  const [enCours, setEnCours] = useState(false);
  const [aConfirmer, setAConfirmer] = useState(false);
  const [msg, setMsg] = useState(null);
  const coupes = transporteurs.filter((t) => !t.actif).length;
  const appliquer = async () => {
    setAConfirmer(false);
    setEnCours(true);
    let faits = 0;
    try {
      let r = null;
      for (const t of transporteurs) {
        r = await api.autoRegler(t.carrierCode, { carrierName: t.carrierName, actif: true, heures: [heure], jours: t.jours.length ? t.jours : [0, 1, 2, 3, 4], copies: t.copies, reessai: t.reessai });
        faits += 1;
      }
      const note = r?.notes?.[0];
      setMsg({ ton: note ? 'warn' : 'ok', texte: `${note ? note + ' ' : ''}Tous les transporteurs (${transporteurs.length}) : automatique à ${r?.reglage?.heures?.[0] || heure} — leurs autres heures sont remplacées.` });
    } catch (e) {
      setMsg({ ton: 'crit', texte: `${errMsg(e)} — ${faits} transporteur${faits > 1 ? 's' : ''} sur ${transporteurs.length} déjà réglé${faits > 1 ? 's' : ''} à la nouvelle heure (voir leurs fiches).` });
    } finally { await onEnregistre(); setEnCours(false); }
  };
  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.line}` }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 11.5, color: C.ink2, minWidth: 130 }}>⚙ Même heure pour tous</span>
        <input type="time" step="300" value={heure} onChange={(e) => setHeure(e.target.value)} className="ba-cible"
          style={{ padding: '4px 6px', border: `1.5px solid ${C.lineStrong}`, borderRadius: 7, font: 'inherit' }} />
        <button type="button" className="ba-cible" style={btn2} disabled={enCours || !transporteurs.length || !heure} onClick={() => setAConfirmer(true)}>
          {enCours ? '⏳ Enregistrement…' : 'Appliquer à tous les transporteurs'}
        </button>
      </div>
      {aConfirmer && (
        <div style={msgStyle('warn')}>
          Les {transporteurs.length} transporteurs passeront en automatique à {heure} (leurs autres heures remplacées)
          {coupes ? <>, dont <b>{coupes} aujourd'hui coupé{coupes > 1 ? 's' : ''}</b> qui {coupes > 1 ? 'seront' : 'sera'} rallumé{coupes > 1 ? 's' : ''}</> : ''}.
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button type="button" className="ba-cible" style={btn2} onClick={() => setAConfirmer(false)}>Annuler</button>
            <button type="button" className="ba-cible" style={btn} onClick={appliquer}>Oui, appliquer à tous</button>
          </div>
        </div>
      )}
      {msg && <div style={msgStyle(msg.ton)}>{msg.texte}</div>}
    </div>
  );
}

/** Dans la fiche du transporteur : sa génération automatique (réglable avec le droit, sinon une phrase). */
export function ReglagesAuto({ carrierCode, carrierName, peutRegler }) {
  const [t, setT] = useState(null);
  const [erreur, setErreur] = useState('');
  const charger = useCallback(async () => {
    try {
      const e = await api.auto();
      setT(e.transporteurs.find((x) => x.carrierCode === String(carrierCode))
        || { carrierCode: String(carrierCode), carrierName, actif: false, heures: [], jours: [0, 1, 2, 3, 4], copies: 1, reessai: false, prochaine: null, prochainJour: null });
      setErreur('');
    } catch (err) { setErreur(errMsg(err)); }
  }, [carrierCode, carrierName]);
  useEffect(() => { charger(); }, [charger]);
  if (erreur) return <div style={{ ...msgStyle('crit'), marginBottom: 12 }}>{erreur}</div>;
  if (!t) return null;
  if (!peutRegler) return <div style={{ fontSize: 12.5, color: C.ink2, margin: '12px 2px' }}>⏰ {resumeReglage(t)}</div>;
  return <div style={{ marginTop: 12 }}><style>{CSS}</style><FicheAuto t={t} peutRegler onEnregistre={charger} /></div>;
}

function Journal({ journal, occupe, impression, onAnnulerGeneration }) {
  return (
    <div style={card}>
      {impression && !impression.disponible && (
          <div style={{ ...msgStyle('warn'), marginTop: 0, marginBottom: 6 }}>
            {impression.configuree === false ? "L'impression automatique n'est pas configurée" : "L'agent d'impression du dépôt (server-home) ne répond pas"} :
            les bordereaux sont quand même créés ; l'impression attend l'agent (au plus 1 h).
          </div>
      )}
      {occupe && <div style={{ ...msgStyle('neutre'), marginTop: 0, marginBottom: 6 }}>⏳ En cours : {occupe.quoi}{occupe.attente ? ' — attend que le robot EffiTrace ait fini' : ''}…</div>}
      <details open={ecranLarge()}>
        <summary style={{ fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>🕘 Journal des générations ({journal.length})</summary>
        <div style={{ fontSize: 11.5, color: C.ink3, margin: '2px 0 6px' }}>Dernières 24 h, automatiques et manuelles, les plus récentes en haut.</div>
        <ul style={{ padding: 0, margin: 0, maxHeight: 420, overflow: 'auto', listStyle: 'none' }}>
          {journal.length ? journal.map((j) => {
            const { texte, ton } = texteJournal(j);
            return (
              <li key={j.id} style={{ padding: '6px 0', borderTop: '1px solid #EDF2F5', fontSize: 12.5 }}>
                <span style={{ ...mono, color: C.ink3 }}>{heureBxl(j.debut)}</span>{' '}
                <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 10, fontWeight: 600, background: j.mode === 'AUTO' || j.mode === 'REESSAI' ? C.accentSoft : C.sunken, color: j.mode === 'AUTO' || j.mode === 'REESSAI' ? C.accent : C.ink2 }}>
                  {LIBELLE_MODE[j.mode] || j.mode}
                </span>{' '}
                <b>{j.carrierName}</b> — <span style={{ color: TONS[ton][0] }}>{texte}</span>
                {onAnnulerGeneration && j.aAnnuler?.length > 0 && (
                  <button type="button" className="ba-cible" onClick={() => onAnnulerGeneration(j)}
                    style={{ ...btn2, padding: '2px 8px', fontSize: 12, marginLeft: 6, color: C.crit, borderColor: '#E2B4AF' }}>
                    ✖ annuler cette génération ({j.aAnnuler.length})
                  </button>
                )}
              </li>
            );
          }) : <li style={{ color: C.ink3, fontSize: 12.5 }}>Rien dans les dernières 24 h.</li>}
        </ul>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------
// Réglages d'un transporteur (dans sa fiche)
// ---------------------------------------------------------------------
function FicheAuto({ t, peutRegler, onEnregistre }) {
  const [ouvert, setOuvert] = useState(ecranLarge);
  const [nouvelle, setNouvelle] = useState('');
  const [msg, setMsg] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const enregistrer = async (changement, ok) => {
    setEnCours(true);
    try {
      const r = await api.autoRegler(t.carrierCode, { carrierName: t.carrierName, actif: t.actif, heures: t.heures, jours: t.jours, copies: t.copies, reessai: t.reessai, ...changement });
      setMsg(r.notes.length ? { ton: 'warn', texte: r.notes.join(' ') } : ok ? { ton: 'ok', texte: ok } : null);
      await onEnregistre();   // le changement suivant part de l'état relu (jamais d'un état périmé)
    } catch (e) { setMsg({ ton: 'crit', texte: errMsg(e) }); } finally { setEnCours(false); }
  };
  const ajouter = () => {
    if (!nouvelle) { setMsg({ ton: 'crit', texte: "Choisis d'abord une heure." }); return; }
    if (t.heures.includes(nouvelle)) { setMsg({ ton: 'crit', texte: `${nouvelle} est déjà prévue pour ${t.carrierName}.` }); return; }
    enregistrer({ heures: [...t.heures, nouvelle] }, `${nouvelle} ajoutée.${t.actif ? '' : ' Active « Auto » pour la lancer.'}`);
    setNouvelle('');
  };
  const retirer = (h) => {
    const heures = t.heures.filter((x) => x !== h);
    enregistrer({ heures, actif: heures.length ? t.actif : false }, !heures.length && t.actif ? 'Plus aucune heure : automatique coupé.' : null);
  };
  const basculerJour = (j) => {
    if (t.jours.includes(j) && t.jours.length === 1 && t.actif) { setMsg({ ton: 'crit', texte: "Au moins un jour tant que l'automatique est actif (sinon coupe « Auto »)." }); return; }
    enregistrer({ jours: t.jours.includes(j) ? t.jours.filter((x) => x !== j) : [...t.jours, j].sort() });
  };
  const peutActiver = t.heures.length > 0 && t.jours.length > 0;
  const bloque = !peutRegler || enCours;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '5px 1fr', gap: 10, border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden', marginBottom: 8, background: '#fff' }}>
      <span style={{ background: teinte(t.carrierName) }} />
      <div style={{ padding: '10px 12px 10px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div><b style={{ fontSize: 15 }}>⏰ Génération automatique</b> <span style={{ fontSize: 12, color: C.ink2, marginLeft: 6 }}>lun–ven, à heure fixe</span></div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={k}>Auto</span>
            <button type="button" aria-label={`Automatique ${t.carrierName}`} aria-pressed={t.actif}
              disabled={bloque || (!t.actif && !peutActiver)} title={!peutRegler ? 'Droit « Paramétrer la génération automatique » requis' : !t.actif && !peutActiver ? 'Ajoute au moins une heure et un jour' : ''}
              onClick={() => enregistrer({ actif: !t.actif })}
              style={{ position: 'relative', width: 46, height: 28, borderRadius: 14, border: 0, cursor: 'pointer', background: t.actif ? C.accent : C.lineStrong, opacity: bloque || (!t.actif && !peutActiver) ? 0.45 : 1, flex: '0 0 auto' }}>
              <span style={{ position: 'absolute', top: 3, left: t.actif ? 21 : 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
            </button>
          </div>
        </div>

        {ouvert && (
          <>
            <Rang lbl="Heures">
              {t.heures.length ? t.heures.map((h) => (
                <span key={h} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 4px 3px 9px', borderRadius: 20, background: C.accentSoft, color: C.accent, fontWeight: 700, fontSize: 13 }}>
                  {h}
                  {peutRegler && <button type="button" className="ba-cible" disabled={enCours} onClick={() => retirer(h)} title="Retirer"
                    style={{ border: 0, background: 'transparent', color: C.accent, cursor: 'pointer', fontSize: 16, padding: '0 6px' }}>×</button>}
                </span>
              )) : <span style={{ fontSize: 12, color: C.ink2 }}>aucune</span>}
              {peutRegler && (
                <>
                  <input type="time" step="300" value={nouvelle} onChange={(e) => setNouvelle(e.target.value)} className="ba-cible"
                    style={{ padding: '4px 6px', border: `1.5px solid ${C.lineStrong}`, borderRadius: 7, font: 'inherit' }} />
                  <button type="button" className="ba-cible" style={btn2} disabled={enCours} onClick={ajouter}>+ Ajouter</button>
                </>
              )}
            </Rang>
            <Rang lbl="Jours">
              {JOURS.map((j, i) => {
                const on = t.jours.includes(i);
                return (
                  <button key={j} type="button" className="ba-chip" disabled={bloque} onClick={() => basculerJour(i)}
                    style={{ padding: '4px 9px', borderRadius: 20, border: `1.5px solid ${on ? C.accent : C.lineStrong}`, background: on ? C.accent : '#fff', color: on ? '#fff' : C.ink, fontWeight: on ? 600 : 500, fontSize: 12.5, cursor: bloque ? 'not-allowed' : 'pointer' }}>{j}</button>
                );
              })}
              {['Sa', 'Di'].map((j) => (
                <span key={j} className="ba-chip" title="Samedi et dimanche : génération à la main seulement"
                  style={{ padding: '4px 9px', borderRadius: 20, border: `1.5px solid ${C.lineStrong}`, fontSize: 12.5, opacity: 0.5 }}>{j}</span>
              ))}
              <span style={{ fontSize: 12, color: C.ink2 }}>week-end : à la main seulement</span>
            </Rang>
            <Rang lbl="Impression">
              <select value={t.copies} disabled={bloque} onChange={(e) => enregistrer({ copies: Number(e.target.value) })} className="ba-cible"
                style={{ padding: '4px 6px', border: `1.5px solid ${C.lineStrong}`, borderRadius: 7, font: 'inherit', background: '#fff' }}>
                <option value={1}>1 exemplaire</option><option value={2}>2 exemplaires</option>
              </select>
              <span style={{ fontSize: 12, color: C.ink2 }}>Konica 2100 (A4), par l'agent d'impression du dépôt</span>
            </Rang>
            <Rang lbl="Réessai">
              <label style={{ fontSize: 12.5, cursor: bloque ? 'not-allowed' : 'pointer' }}>
                <input type="checkbox" checked={t.reessai} disabled={bloque} onChange={(e) => enregistrer({ reessai: e.target.checked })} />{' '}
                S'il reste des colis non contrôlés, réessayer <b>une fois</b> 30 min plus tard
              </label>
            </Rang>
          </>
        )}
        <div style={{ fontSize: 12, color: C.ink2, marginTop: 8 }}>{resumeReglage(t)}</div>
        {msg && <div style={msgStyle(msg.ton)}>{msg.texte}</div>}
        <button type="button" className="ba-cible" onClick={() => setOuvert(!ouvert)}
          style={{ border: 0, background: 'transparent', color: C.accent, font: 'inherit', fontWeight: 600, cursor: 'pointer', padding: '6px 0 0' }}>
          {ouvert ? '▴ Fermer les réglages' : '⚙ Réglages (heures, jours, impression, réessai) ▾'}
        </button>
      </div>
    </div>
  );
}

function Rang({ lbl, children }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
      <span style={{ fontSize: 11.5, color: C.ink2, minWidth: 78 }}>{lbl}</span>{children}
    </div>
  );
}

// ---------------------------------------------------------------------
// Annuler des bordereaux (28-10-2026) — non signés : un, plusieurs, tous, une génération ; ou une remise SIGNÉE du jour
// ---------------------------------------------------------------------
/**
 * items : [{ id, numero, clientName, carrierName, parcels, lot? }] ; lots : générations proposées en raccourci ;
 * remise : { id, driverName, signedAt } → annule la remise signée (toutes ses lignes) au lieu des bordereaux un par un.
 * onFait(texte, ton) une fois fait (même partiellement : le texte dit ce qui n'a pas pu l'être).
 */
const delai = (err) => err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''));
export function AnnulerDialog({ titre, items, lots = [], remise = null, cocherTout = true, onFermer, onFait }) {
  // « tous les transporteurs » s'ouvre sans rien de coché : on choisit ce qu'on annule, jamais tout le quai par défaut
  const [coches, setCoches] = useState(() => new Set(cocherTout ? items.map((b) => b.id) : []));
  const [raison, setRaison] = useState('');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [tente, setTente] = useState(false);   // un essai a parlé au serveur : à la fermeture, l'appelant relit
  const [resultat, setResultat] = useState('');   // dernière réponse du serveur (une erreur de saisie ne la remplace pas)
  const fermer = () => { if (tente) onFait(resultat || 'Annulation non faite.', 'crit'); else onFermer(); };
  const choisis = remise ? items : items.filter((b) => coches.has(b.id));
  const colis = choisis.reduce((t, b) => t + (b.parcels || 0), 0);
  const groupes = [...new Set(items.map((b) => b.carrierName))].map((nom) => [nom, items.filter((b) => b.carrierName === nom)]);
  const basculer = (id) => setCoches((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const valider = async () => {
    if (!choisis.length) { setErreur('Cochez au moins un bordereau.'); return; }
    if (!raison.trim()) { setErreur('La raison est obligatoire : elle reste dans l’archive.'); return; }
    setErreur(''); setEnCours(true); setTente(true);
    try {
      if (remise) {
        const r = await api.cancelHandover(remise.id, raison.trim());
        onFait(r.message || 'Remise annulée.', 'ok');
        return;
      }
      // par lots (une requête reste sous son délai) ; un lot en erreur arrête la suite, ce qui est fait est dit.
      // Délai dépassé : Zeus a pu continuer côté serveur → on le dit, on ne prétend pas « rien fait ».
      const r = { annules: [], echecs: [] };
      const ids = choisis.map((b) => b.id);
      for (let i = 0; i < ids.length; i += ANNULATION_LOT) {
        try {
          const x = await api.annuler(ids.slice(i, i + ANNULATION_LOT), raison.trim());
          r.annules.push(...x.annules); r.echecs.push(...x.echecs);
        } catch (err) {
          const reste = ids.length - i;
          const pourquoi = delai(err) ? 'délai dépassé : Zeus a peut-être continué — rouvrez la liste avant de recommencer' : errMsg(err);
          if (!r.annules.length && !r.echecs.length && !delai(err)) throw err;
          r.echecs.push({ numero: '', carrierName: `${reste} bordereau${reste > 1 ? 'x' : ''} pas confirmé${reste > 1 ? 's' : ''}`, message: pourquoi });
          break;
        }
      }
      const n = r.annules.length;
      const texte = (n ? `${n} bordereau${n > 1 ? 'x' : ''} annulé${n > 1 ? 's' : ''} dans EffiTrace (${r.annules.reduce((t, b) => t + (b.parcels || 0), 0)} colis revenus en attente, à régénérer).` : 'Aucun bordereau annulé.')
        + (r.echecs.length ? ` Pas annulé : ${r.echecs.map(texteEchec).join(' ; ')}.` : '');
      // délai dépassé : le serveur a pu tout faire → on ferme et la liste se relit (elle dira ce qui reste)
      if (r.echecs.some((x) => /délai dépassé/.test(x.message))) {
        onFait(`${n ? texte : 'Délai dépassé : Zeus a peut-être annulé ces bordereaux en arrière-plan.'} La liste est relue : vérifiez ce qui reste avant de recommencer.`, 'warn');
        return;
      }
      if (!n) { setErreur(texte); setResultat(texte); setEnCours(false); return; }
      onFait(texte, r.echecs.length ? 'warn' : 'ok');
    } catch (err) {
      if (remise && delai(err)) { onFait('Délai dépassé : Zeus a peut-être annulé la remise en arrière-plan. Vérifiez avant de recommencer.', 'warn'); return; }
      setErreur(errMsg(err)); setResultat(errMsg(err)); setEnCours(false);
    }
  };
  return (
    <div onClick={(e) => { if (e.target === e.currentTarget && !enCours) fermer(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(22,32,43,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, zIndex: 50 }}>
      <div style={{ background: '#fff', borderRadius: 12, maxWidth: 640, width: '100%', padding: 16, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{titre}</div>
        <div style={{ fontSize: 11.5, color: C.ink3, margin: '2px 0 8px' }}>
          {remise
            ? `Remise signée par ${remise.driverName} à ${heureBxl(remise.signedAt)} : la sortie est annulée dans EffiTrace ; la preuve signée reste dans l’archive, marquée « annulée ».`
            : 'Pour chaque bordereau coché, Zeus fait le geste EffiTrace qui convient (déclôturer s’il n’est pas sorti, sinon annuler la sortie), puis le marque « annulé » : rien n’est effacé, la trace reste dans l’archive.'}
        </div>
        {!remise && items.length > 1 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, color: C.ink2 }}>Cocher :</span>
            <button type="button" className="ba-chip" style={btn2} onClick={() => setCoches(new Set(items.map((b) => b.id)))}>tout</button>
            <button type="button" className="ba-chip" style={btn2} onClick={() => setCoches(new Set())}>rien</button>
            {lots.map((l) => (
              <button key={l} type="button" className="ba-chip" style={btn2} onClick={() => setCoches(new Set(items.filter((b) => b.lot === l).map((b) => b.id)))}>génération {l}</button>
            ))}
          </div>
        )}
        {groupes.map(([nom, lst]) => (
          <div key={nom} style={{ marginTop: 8 }}>
            <div style={{ ...k, marginBottom: 2 }}>{nom}</div>
            {lst.map((b) => (
              <label key={b.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 2px', borderTop: `1px solid ${C.line}`, cursor: remise ? 'default' : 'pointer' }}>
                {!remise && <input type="checkbox" checked={coches.has(b.id)} onChange={() => basculer(b.id)} disabled={enCours} style={{ width: 22, height: 22 }} />}
                <span style={{ flex: 1, fontSize: 13 }}>
                  <b>{b.clientName}</b> <span style={mono}>n° {b.numero}</span> · {b.parcels} colis
                  {b.lot && <span style={{ fontSize: 11.5, color: C.ink3 }}> · généré {b.lot}</span>}
                  {b.enVerification && <span style={{ fontSize: 11.5, color: C.warn }}> · annulation envoyée, à confirmer</span>}
                </span>
              </label>
            ))}
          </div>
        ))}
        <div style={{ ...k, marginTop: 12 }}>Raison (obligatoire)</div>
        <input type="text" value={raison} onChange={(e) => setRaison(e.target.value)} disabled={enCours} maxLength={300} className="ba-cible"
          placeholder="ex. généré par erreur, camion annulé, erreur de transporteur…"
          style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, padding: 9, border: `1.5px solid ${C.lineStrong}`, borderRadius: 8, font: 'inherit' }} />
        {erreur && <div style={msgStyle('crit')}>{erreur}</div>}
        <div style={msgStyle('warn')}>Les colis reviennent « en attente » chez EffiTrace : il faudra régénérer un bordereau. Une feuille déjà imprimée n’est plus valable — à jeter.</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
          <button type="button" className="ba-cible" style={btn2} onClick={fermer} disabled={enCours}>{tente ? 'Fermer' : 'Ne rien annuler'}</button>
          <button type="button" className="ba-cible" disabled={enCours || !choisis.length} onClick={valider}
            style={{ ...btn, background: enCours || !choisis.length ? C.lineStrong : C.crit }}>
            {enCours ? '⏳ Annulation dans EffiTrace…' : remise ? '✖ Annuler la remise' : `✖ Annuler ${choisis.length} bordereau${choisis.length > 1 ? 'x' : ''} (${colis} colis)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Sortir tout SANS signature (28-10-2026, temporaire : le temps d'habituer les chauffeurs à signer sur le PDA)
// ---------------------------------------------------------------------
function SortieDialog({ items, onFermer, onFait }) {
  const [coches, setCoches] = useState(() => new Set(items.map((b) => b.id)));   // « tout sortir » : tout coché, décochable
  const [raison, setRaison] = useState('');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [tente, setTente] = useState(false);
  const [resultat, setResultat] = useState('');   // dernière réponse du serveur (une erreur de saisie ne la remplace pas)
  const choisis = items.filter((b) => coches.has(b.id));
  const colis = choisis.reduce((t, b) => t + (b.parcels || 0), 0);
  const groupes = [...new Set(items.map((b) => b.carrierName))].map((nom) => [nom, items.filter((b) => b.carrierName === nom)]);
  const basculer = (ids, oui) => setCoches((c) => { const n = new Set(c); for (const id of ids) { if (oui) n.add(id); else n.delete(id); } return n; });
  const fermer = () => { if (tente) onFait(resultat || 'Rien n’a été sorti.', 'crit'); else onFermer(); };
  const valider = async () => {
    if (!choisis.length) { setErreur('Cochez au moins un bordereau.'); return; }
    if (!raison.trim()) { setErreur('Indiquez pourquoi le chauffeur ne signe pas — la raison reste dans l’archive.'); return; }
    setErreur(''); setEnCours(true); setTente(true);
    try {
      // par paquets (le serveur n'en prend pas plus d'un coup) ; un paquet en erreur arrête la suite, ce qui est fait est dit ;
      // délai dépassé : le serveur a pu finir → on ne prétend pas « pas sorti », la liste se relit
      const r = { sortis: [], echecs: [] };
      const ids = choisis.map((b) => b.id);
      for (let i = 0; i < ids.length; i += SORTIE_LOT) {
        try {
          const x = await api.sortirSansSignature(ids.slice(i, i + SORTIE_LOT), raison.trim());
          r.sortis.push(...x.sortis); r.echecs.push(...x.echecs);
        } catch (err) {
          if (delai(err)) {
            onFait(`${r.sortis.length ? `${r.sortis.length} sorti${r.sortis.length > 1 ? 's' : ''} ; ` : ''}délai dépassé : Zeus a peut-être fini la sortie en arrière-plan. La liste est relue : vérifiez ce qui reste avant de recommencer.`, 'warn');
            return;
          }
          if (!r.sortis.length && !r.echecs.length) throw err;
          // « … la sortie a peut-être été faite » (envoi interrompu, réponse HTTP, page non relue) : jamais rangés dans « pas sorti »
          r.echecs.push({ numero: '', carrierName: `${ids.length - i} restants`, message: errMsg(err), sortiEft: /peut-être été faite/.test(errMsg(err)) });   // les 3 erreurs « peut-être faite » du serveur
          break;
        }
      }
      const n = r.sortis.length;
      // « sortis chez EffiTrace mais pas notés » ≠ « pas sortis » : dits séparément
      const nonNotes = r.echecs.filter((x) => x.sortiEft);
      const pasSortis = r.echecs.filter((x) => !x.sortiEft);
      const texte = (n ? `${n} bordereau${n > 1 ? 'x' : ''} sorti${n > 1 ? 's' : ''} chez EffiTrace sans signature (${r.sortis.reduce((t, b) => t + (b.parcels || 0), 0)} colis).` : 'Aucun bordereau noté sorti.')
        + (nonNotes.length ? ` Sortis (ou peut-être sortis) chez EffiTrace mais pas notés dans Zeus : ${nonNotes.map(texteEchec).join(' ; ')}.` : '')
        + (pasSortis.length ? ` Pas sorti : ${pasSortis.map(texteEchec).join(' ; ')}.` : '');
      if (!n && !nonNotes.length) { setErreur(texte); setResultat(texte); setEnCours(false); return; }
      onFait(texte, r.echecs.length ? 'warn' : 'ok');
    } catch (err) {
      if (delai(err)) { onFait('Délai dépassé : Zeus a peut-être fini la sortie en arrière-plan. La liste est relue : vérifiez ce qui reste avant de recommencer.', 'warn'); return; }
      setErreur(errMsg(err)); setResultat(errMsg(err)); setEnCours(false);
    }
  };
  return (
    <div onClick={(e) => { if (e.target === e.currentTarget && !enCours) fermer(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(22,32,43,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, zIndex: 50 }}>
      <div style={{ background: '#fff', borderRadius: 12, maxWidth: 640, width: '100%', padding: 16, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>Sortir sans signature du chauffeur ?</div>
        <div style={{ fontSize: 11.5, color: C.ink3, margin: '2px 0 8px' }}>
          Zeus sort chez EffiTrace les bordereaux cochés, comme après une signature, mais sans preuve signée : l’archive garde seulement qui, quand et pourquoi.
          Temporaire, le temps que les chauffeurs signent sur le PDA.
        </div>
        {items.length > 1 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, color: C.ink2 }}>Cocher :</span>
            <button type="button" className="ba-chip" style={btn2} onClick={() => setCoches(new Set(items.map((b) => b.id)))} disabled={enCours}>tout</button>
            <button type="button" className="ba-chip" style={btn2} onClick={() => setCoches(new Set())} disabled={enCours}>rien</button>
          </div>
        )}
        {groupes.map(([nom, lst]) => {
          const tous = lst.every((b) => coches.has(b.id));
          return (
            <div key={nom} style={{ marginTop: 8 }}>
              <label style={{ ...k, display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', marginBottom: 2 }}>
                <input type="checkbox" checked={tous} onChange={() => basculer(lst.map((b) => b.id), !tous)} disabled={enCours} style={{ width: 18, height: 18 }} />
                {nom} · {lst.length} bordereau{lst.length > 1 ? 'x' : ''}
              </label>
              {lst.map((b) => (
                <label key={b.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 2px', borderTop: `1px solid ${C.line}`, cursor: 'pointer' }}>
                  <input type="checkbox" checked={coches.has(b.id)} onChange={() => basculer([b.id], !coches.has(b.id))} disabled={enCours} style={{ width: 22, height: 22 }} />
                  <span style={{ flex: 1, fontSize: 13 }}><b>{b.clientName}</b> <span style={mono}>n° {b.numero}</span> · {b.parcels} colis</span>
                </label>
              ))}
            </div>
          );
        })}
        <div style={{ ...k, marginTop: 12 }}>Raison (obligatoire)</div>
        <input type="text" value={raison} onChange={(e) => setRaison(e.target.value)} disabled={enCours} maxLength={300} className="ba-cible"
          placeholder="ex. chauffeur pressé, PDA indisponible…"
          style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, padding: 9, border: `1.5px solid ${C.lineStrong}`, borderRadius: 8, font: 'inherit' }} />
        {erreur && <div style={msgStyle('crit')}>{erreur}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
          <button type="button" className="ba-cible" style={btn2} onClick={fermer} disabled={enCours}>{tente ? 'Fermer' : 'Ne rien sortir'}</button>
          <button type="button" className="ba-cible" disabled={enCours || !choisis.length} onClick={valider}
            style={{ ...btn, background: enCours || !choisis.length ? C.lineStrong : C.warn }}>
            {enCours ? '⏳ Sortie chez EffiTrace…' : `🚚 Sortir ${choisis.length} bordereau${choisis.length > 1 ? 'x' : ''} sans signature (${colis} colis)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Confirmation
// ---------------------------------------------------------------------
function Confirmation({ confirme, colis, onAnnuler, onOk }) {
  const lignes = (colis?.lignes || []).filter((l) => l.colis > 0 && (confirme.tous || l.carrierCode === confirme.t.carrierCode));
  const ctl = lignes.reduce((s, l) => s + l.controles, 0);
  const non = lignes.reduce((s, l) => s + l.aControler, 0);
  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onAnnuler(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(22,32,43,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 }}>
      <div style={{ background: '#fff', borderRadius: 12, maxWidth: 620, width: '100%', padding: 18, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{confirme.tous ? 'Générer + imprimer pour tous les transporteurs ?' : `${confirme.t.carrierName} : générer + imprimer maintenant ?`}</div>
        <div style={{ fontSize: 11.5, color: C.ink3, margin: '2px 0 8px' }}>D'après la dernière lecture d'EffiTrace{colis?.luLe ? ` (${heureBxl(colis.luLe)})` : ''} — Zeus relit juste avant de créer.</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr><th style={{ ...th, textAlign: 'left' }}>Transporteur</th><th style={th}>Sur bordereau</th><th style={th}>Restent</th><th style={{ ...th, textAlign: 'left' }}>Résultat</th></tr></thead>
          <tbody>{lignes.map((l) => (
            <tr key={l.carrierCode}>
              <td style={{ ...td, textAlign: 'left' }}>{l.carrierName}</td><td style={{ ...td, ...mono }}>{l.controles}</td><td style={{ ...td, ...mono }}>{l.aControler || '—'}</td>
              <td style={{ ...td, textAlign: 'left', color: l.controles ? C.ok : C.warn }}>{l.controles ? 'généré + imprimé' : 'sauté : rien de contrôlé'}</td>
            </tr>
          ))}</tbody>
          <tfoot><tr><td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>Total</td><td style={{ ...td, ...mono, fontWeight: 700 }}>{ctl}</td><td style={{ ...td, ...mono, fontWeight: 700 }}>{non || '—'}</td><td style={td} /></tr></tfoot>
        </table>
        {non > 0 && <div style={msgStyle('warn')}>{non} colis ne sont pas encore contrôlés au packing : ils resteront en attente pour un prochain bordereau.</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
          <button type="button" className="ba-cible" style={btn2} onClick={onAnnuler}>Annuler</button>
          <button type="button" className="ba-cible" style={{ ...btn, ...(ctl ? {} : { background: C.lineStrong }) }} disabled={!ctl} onClick={onOk}>
            Oui, générer + imprimer ({ctl} colis)
          </button>
        </div>
      </div>
    </div>
  );
}
